import { useEffect, useRef, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { Field } from "./fields";

import { currencyFractionDigits, currencyScale } from "./money-format";
export { currencyFractionDigits, currencyScale, formatMoneyMinor } from "./money-format";

export function minorToMajorInput(minor: number, currency: string): string {
  const digits = currencyFractionDigits(currency);
  const scale = currencyScale(currency);
  return (minor / scale).toFixed(digits);
}

export function majorInputToMinor(
  raw: string,
  currency: string,
  options: { allowNegative?: boolean } = {}
): number | null {
  let normalized = raw.trim();
  if (normalized === "") return null;

  // Normalize Arabic/Persian decimal input without changing the stored integer model.
  normalized = normalized
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)))
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/٫/g, ".");

  // Accept a comma as a decimal separator only when no decimal point is present.
  if (!normalized.includes(".") && normalized.includes(",")) {
    normalized = normalized.replace(",", ".");
  }

  const negative = normalized.startsWith("-");
  if (negative && !options.allowNegative) return null;
  if (negative) normalized = normalized.slice(1);

  const digits = currencyFractionDigits(currency);
  const pattern = digits === 0
    ? /^\d+$/
    : new RegExp(`^\\d+(?:\\.\\d{0,${digits}})?$`);

  if (!pattern.test(normalized)) return null;

  const [wholePart, fractionPart = ""] = normalized.split(".");
  const scale = BigInt(10) ** BigInt(digits);
  const whole = BigInt(wholePart);
  const fractionText = digits === 0
    ? "0"
    : (fractionPart + "0".repeat(digits)).slice(0, digits) || "0";
  const fraction = BigInt(fractionText);
  let minor = whole * scale + fraction;
  if (negative) minor = -minor;

  const max = BigInt(Number.MAX_SAFE_INTEGER);
  const min = BigInt(Number.MIN_SAFE_INTEGER);
  if (minor > max || minor < min) return null;
  return Number(minor);
}

type MoneyFieldProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "defaultValue" | "onChange" | "type"
> & {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  valueMinor: number | null | undefined;
  currency?: string;
  nullable?: boolean;
  allowNegative?: boolean;
  invalidMessage?: ReactNode;
  onMinorChange: (value: number | null) => void;
};

/**
 * User-facing monetary input. The UI always speaks major currency units while
 * callers continue to own integer minor units at the API/domain boundary.
 */
export function MoneyField({
  label,
  hint,
  error,
  valueMinor,
  currency = "AED",
  nullable = false,
  allowNegative = false,
  invalidMessage,
  onMinorChange,
  onBlur,
  ...props
}: MoneyFieldProps) {
  const [raw, setRaw] = useState(() =>
    valueMinor == null ? "" : minorToMajorInput(valueMinor, currency)
  );
  const [invalid, setInvalid] = useState(false);
  const lastEmitted = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    if (lastEmitted.current === valueMinor) {
      lastEmitted.current = undefined;
      return;
    }
    setRaw(valueMinor == null ? "" : minorToMajorInput(valueMinor, currency));
    setInvalid(false);
  }, [valueMinor, currency]);

  const digits = currencyFractionDigits(currency);
  const defaultInvalidMessage = `Enter a valid ${currency} amount with up to ${digits} decimal place${digits === 1 ? "" : "s"}.`;

  return (
    <Field
      {...props}
      label={label}
      hint={hint}
      error={error ?? (invalid ? (invalidMessage ?? defaultInvalidMessage) : undefined)}
      type="text"
      inputMode="decimal"
      value={raw}
      suffix={currency}
      onChange={(event) => {
        const nextRaw = event.target.value;
        setRaw(nextRaw);

        if (nextRaw.trim() === "") {
          setInvalid(!nullable);
          if (nullable) {
            lastEmitted.current = null;
            onMinorChange(null);
          }
          return;
        }

        const parsed = majorInputToMinor(nextRaw, currency, { allowNegative });
        setInvalid(parsed == null);
        if (parsed != null) {
          lastEmitted.current = parsed;
          onMinorChange(parsed);
        }
      }}
      onBlur={(event) => {
        if (invalid || raw.trim() === "") {
          if (!nullable && valueMinor != null) {
            setRaw(minorToMajorInput(valueMinor, currency));
            setInvalid(false);
          }
        } else if (valueMinor != null) {
          setRaw(minorToMajorInput(valueMinor, currency));
        }
        onBlur?.(event);
      }}
    />
  );
}
